/**
 * The SHIPYARD — a ship SELECTION screen (2026-08-10 rework).
 *
 * The part-by-part builder is retired for now: ships are no longer editable.
 * What remains is the part players actually lived in — the void, the slowly
 * orbiting 3D ship, and the hangar cards — promoted to the whole screen.
 * Clicking a card boards that ship immediately (the live preview swaps to it
 * and the selection persists through the caller's board handler); DONE flies.
 *
 * The hangar list is assembled by the CALLER (main.ts): the stock frame, every
 * EARNED GLB hull (`ship:` collection tokens — virtual cards, no Firestore doc
 * behind them), and any saved ships from the builder era. Only the last kind is
 * deletable — you cannot delete the stock frame or an earned entitlement.
 *
 * Local ship state (localStorage) and thumbnails keep their existing seams
 * (saveLocalLayout in the board handler, shipThumb per card).
 */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { layoutHull, type ShipLayout } from "@hyperlight/shared";
import type { ShipDoc } from "../auth/firebase";
import { shipThumb } from "../ui/thumbs";

export interface ShipyardHandlers {
  /** Close-out (the caller resumes flight; the selection already applied). */
  onDone: () => void;
  hangar: {
    /** Every boardable ship, virtual cards first (stock, earned hulls). */
    list: () => Promise<ShipDoc[]>;
    /** Apply + persist the selection; returns the freshly built live group. */
    board: (doc: ShipDoc) => THREE.Group;
    /** Delete a SAVED ship doc (never offered on virtual cards). */
    remove: (id: string) => Promise<ShipDoc[]>;
    /** The layout currently flying — marks its card as selected. */
    current: () => ShipLayout;
  };
}

/** Virtual cards (stock frame, earned hulls) carry an id prefix instead of a
 *  Firestore doc id; they cannot be deleted from here. */
const isVirtual = (id: string): boolean => id.startsWith("stock:") || id.startsWith("hull:");

// --- Local ship cache (fast boot before the profile round-trip) -------------

const STORAGE_KEY = "hyperlight:ship";

export interface SavedShip {
  layout: ShipLayout;
  /** Epoch ms of the save — used to reconcile against the cloud profile. */
  t: number;
  /** Account that wrote this cache ("guest" when signed out). A cache from
   *  a DIFFERENT account never reconciles — that was the cross-account
   *  ship-bleed bug. */
  uid?: string;
}

export function loadSavedLayout(): SavedShip | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Legacy format: a bare layout array (no timestamp).
    if (Array.isArray(parsed)) return { layout: parsed as ShipLayout, t: 0 };
    if (parsed && Array.isArray(parsed.layout)) {
      return {
        layout: parsed.layout as ShipLayout,
        t: Number(parsed.t) || 0,
        uid: typeof parsed.uid === "string" ? parsed.uid : undefined,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function saveLocalLayout(layout: ShipLayout, t: number, uid = "guest"): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ layout, t, uid }));
  } catch {
    // Private browsing or full quota — layout just won't persist.
  }
}

export class Shipyard {
  active = false;
  private orbit: OrbitControls;
  private panel: HTMLElement;
  private statusEl: HTMLElement;

  constructor(
    private camera: THREE.PerspectiveCamera,
    private dom: HTMLCanvasElement,
    private handlers: ShipyardHandlers,
  ) {
    this.orbit = this.makeOrbit();
    this.orbit.enabled = false;

    this.panel = document.createElement("div");
    this.panel.id = "shipyard-panel";
    this.panel.style.display = "none";
    this.buildPanel();
    document.body.appendChild(this.panel);
    this.statusEl = this.panel.querySelector("#sy-status")!;
  }

  /** A fresh damped orbit, baking in the camera's CURRENT up-vector (set from
   *  the ship's frame at open — the void has no world up to inherit). */
  private makeOrbit(): OrbitControls {
    const orbit = new OrbitControls(this.camera, this.dom);
    orbit.enableDamping = true;
    orbit.dampingFactor = 0.08;
    orbit.enablePan = false;
    orbit.minDistance = 6;
    orbit.maxDistance = 40;
    return orbit;
  }

  private buildPanel(): void {
    this.panel.innerHTML = `
      <div class="sy-header">SHIPYARD</div>
      <div class="sy-help">Click a ship to board it — the selection is yours the moment it swaps in.</div>
      <div id="sy-hangar">
        <div class="sy-cat-name">YOUR SHIPS</div>
        <div id="sy-ship-list"></div>
      </div>
      <div id="sy-status"></div>
      <div class="sy-actions">
        <button id="sy-done">DONE — FLY</button>
      </div>
    `;
    this.panel.querySelector("#sy-done")!.addEventListener("click", () => this.done());
  }

  open(shipGroup: THREE.Group, focus: THREE.Vector3): void {
    this.active = true;

    // Align the orbit frame with the ship's current orientation, then rebuild
    // the controls so they bake in the new up-vector (see makeOrbit).
    const shipQuat = shipGroup.quaternion;
    this.camera.up.set(0, 1, 0).applyQuaternion(shipQuat);
    this.orbit.dispose();
    this.orbit = this.makeOrbit();
    this.orbit.enabled = true;
    this.orbit.target.copy(focus);
    // Start from a pleasant three-quarter view, in the ship's frame.
    this.camera.position
      .copy(focus)
      .add(new THREE.Vector3(9, 5, 11).applyQuaternion(shipQuat));

    this.panel.style.display = "flex";
    this.setStatus("");
    void this.handlers.hangar.list().then((docs) => this.refreshHangar(docs));
  }

  /** Rebuild the hangar as selection CARDS, each with a rendered thumb. */
  private refreshHangar(docs: ShipDoc[]): void {
    const listEl = this.panel.querySelector<HTMLElement>("#sy-ship-list")!;
    listEl.innerHTML = "";
    if (docs.length === 0) {
      // NO DEFAULT SHIP: a new character's hangar is honestly empty.
      const empty = document.createElement("div");
      empty.className = "sy-help";
      empty.textContent = "No ships. Registrations are earned — use one from your bag and it will appear here.";
      listEl.appendChild(empty);
      return;
    }
    const current = JSON.stringify(this.handlers.hangar.current());
    for (const doc of docs) {
      const card = document.createElement("div");
      card.className = "hg-card";
      if (JSON.stringify(doc.layout) === current) card.classList.add("hg-active");
      const img = document.createElement("img");
      img.src = shipThumb(doc.layout);
      img.alt = doc.name;
      const name = document.createElement("div");
      name.className = "hg-name";
      name.textContent = doc.name;
      card.append(img, name);
      // Registrations are PERMANENT (2026-08-12, user call — the WoW rule): a
      // claimed hull can never be deleted, and that includes the saved doc the
      // first board materializes around it ("My Ship" wrapping the Lightspeed).
      // Deleting that doc read as deleting the ship — and stranded shipOwned.
      if (!isVirtual(doc.id) && layoutHull(doc.layout) === null) {
        const del = document.createElement("button");
        del.className = "hg-del";
        del.textContent = "✕";
        del.title = "delete ship";
        del.addEventListener("click", (e) => {
          e.stopPropagation(); // card click = board; don't board a deletion
          void this.handlers.hangar
            .remove(doc.id)
            .then((next) => {
              this.refreshHangar(next);
              this.setStatus(`deleted "${doc.name}"`);
            })
            .catch(() => this.setStatus("delete failed"));
        });
        card.appendChild(del);
      }
      card.addEventListener("click", () => {
        this.handlers.hangar.board(doc);
        this.setStatus(`boarded "${doc.name}"`);
        this.refreshHangar(docs); // re-mark the active card
      });
      listEl.appendChild(card);
    }
  }

  private setStatus(text: string): void {
    this.statusEl.textContent = text;
  }

  /** Per-frame while active (damped orbit). */
  update(): void {
    if (!this.active) return;
    this.orbit.update();
  }

  private done(): void {
    this.close();
    this.handlers.onDone();
  }

  close(): void {
    this.active = false;
    this.orbit.enabled = false;
    this.panel.style.display = "none";
  }
}
